import { useEffect, useMemo, useState } from "react";

import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";
import DialogInventarioHistoricoMaterial from "./DialogInventarioHistoricoMaterial";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Draggable from "react-draggable";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import MDTypography from "components/MDTypography";

function PaperComponent(props) {
  return (
    <Draggable handle=".draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const formatData = (v) => (v ? String(v).substring(0, 16).replace("T", " ") : "");

export default function Inventarios() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Tesouraria") || temFuncao("Informática");

  const [loading, setLoading] = useState(false);

  const [centros, setCentros] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [inventarios, setInventarios] = useState([]);

  const [openHistoricoMaterial, setOpenHistoricoMaterial] = useState(false);

  const [modoInventario, setModoInventario] = useState("LIVRE");

  const [centro, setCentro] = useState(null);
  const [fabricante, setFabricante] = useState(null);
  const [marcasSelecionadas, setMarcasSelecionadas] = useState([]);
  const [openNovoInventario, setOpenNovoInventario] = useState(false);
  const [incluirEncerrados, setIncluirEncerrados] = useState(true);

  const [inventarioEliminar, setInventarioEliminar] = useState(null);
  const [eliminarLoading, setEliminarLoading] = useState(false);

  const [inventarioContagens, setInventarioContagens] = useState(null);
  const [contagens, setContagens] = useState([]);
  const [contagensLoading, setContagensLoading] = useState(false);

  // Encerramento sugerido após exportação PDF/Excel.
  // Encerramento após exportação ou diretamente na lista.
  const [inventarioEncerrar, setInventarioEncerrar] = useState(null);
  const [encerrarLoading, setEncerrarLoading] = useState(false);

  // Reabertura diretamente na lista (exclusiva de isAdminLike).
  const [inventarioReabrir, setInventarioReabrir] = useState(null);
  const [motivoReabertura, setMotivoReabertura] = useState("");
  const [reabrirLoading, setReabrirLoading] = useState(false);

  const idutilizador = Number(userData?.id || 0);

  const carregarCentros = async () => {
    const [data, err] = await handleApi(api.post("Stocks/SelInventarioCentrosCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setCentros(
      Array.isArray(data)
        ? data.map((x) => ({
            ...x,
            refcentro: String(x.refcentro || "").trim(),
            centro: String(x.centro || x.refcentro || "").trim(),
          }))
        : []
    );
  };

  const carregarFabricantes = async () => {
    const [data, err] = await handleApi(api.post("Fabricantes/SelFabricantesCombo", {}));

    if (err) {
      showAlert(err, "error");
      return;
    }

    setFabricantes(Array.isArray(data) ? data : []);
  };

  const carregarMarcas = async (reffabricante) => {
    setMarcas([]);
    setMarcasSelecionadas([]);

    if (!reffabricante) return;

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarioMarcasCombo", { reffabricante, term: "" })
    );

    if (err) {
      showAlert(err, "error");
      return;
    }

    setMarcas(Array.isArray(data) ? data : []);
  };

  const carregarInventarios = async () => {
    if (!idutilizador) return;

    setLoading(true);

    const [data, err] = await handleApi(
      api.post("Stocks/SelInventarios", {
        idutilizador,
        incluirEncerrados: incluirEncerrados ? 1 : 0,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setInventarios(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (!idutilizador) return;

    setLoading(true);

    Promise.all([carregarCentros(), carregarFabricantes()]).finally(() => setLoading(false));
  }, [idutilizador]);

  useEffect(() => {
    carregarInventarios();
  }, [idutilizador, incluirEncerrados]);

  // Atualizar a lista quando o utilizador regressa a esta janela.
  useEffect(() => {
    const atualizarAoFocar = () => {
      if (document.visibilityState === "visible" && idutilizador) {
        carregarInventarios();
      }
    };

    window.addEventListener("focus", atualizarAoFocar);

    return () => {
      window.removeEventListener("focus", atualizarAoFocar);
    };
  }, [idutilizador, incluirEncerrados]);

  const criarInventario = async () => {
    if (!centro?.refcentro) {
      showAlert("Seleciona o centro.", "warning");
      return;
    }

    if (modoInventario === "MARCAS" && !fabricante?.ref) {
      showAlert("Seleciona o fabricante.", "warning");
      return;
    }

    if (modoInventario === "MARCAS" && marcasSelecionadas.length === 0) {
      showAlert("Seleciona pelo menos uma marca.", "warning");
      return;
    }

    setLoading(true);

    const [inventario, err, meta] = await handleApi(
      api.post("Stocks/InsInventario", {
        modo: modoInventario,
        refcentro: centro.refcentro,
        reffabricante: modoInventario === "MARCAS" ? fabricante.ref : null,
        refsMarcas:
          modoInventario === "MARCAS" ? marcasSelecionadas.map((m) => String(m.ref).trim()) : [],
        idutilizadorabertura: idutilizador,
        obs: "",
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    showAlert(meta?.message || "Inventário criado.", "success");

    setOpenNovoInventario(false);

    setCentro(null);
    setFabricante(null);
    setMarcasSelecionadas([]);
    setMarcas([]);

    await carregarInventarios();

    if (inventario?.id) {
      window.open(`/stocks/inventarios/${inventario.id}`, "_blank", "noopener,noreferrer");
    }
  };

  const abrirInventario = (row) => {
    if (!row?.id) return;
    window.open(`/stocks/inventarios/${row.id}`, "_blank", "noopener,noreferrer");
  };

  const abrirContagens = async (row) => {
    if (!row?.id || !idutilizador) return;

    setInventarioContagens(row);
    setContagens([]);
    setContagensLoading(true);

    try {
      const [data, err] = await handleApi(
        api.post("Stocks/SelInventarioMateriais", {
          idinventario: row.id,
          idutilizador,
        })
      );

      if (err) {
        showAlert(err, "error");
        setInventarioContagens(null);
        return;
      }

      setContagens(Array.isArray(data?.contados) ? data.contados : []);
    } catch (error) {
      showAlert(error?.message || "Erro ao consultar as contagens.", "error");
      setInventarioContagens(null);
    } finally {
      setContagensLoading(false);
    }
  };

  const eliminarInventario = async () => {
    if (!inventarioEliminar?.id || !isAdminLike || eliminarLoading) return;

    setEliminarLoading(true);

    try {
      const [data, err, meta] = await handleApi(
        api.post("Stocks/DelInventario", {
          id: inventarioEliminar.id,
          idutilizador,
        })
      );

      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Não foi possível eliminar o inventário.", "error");
        return;
      }

      setInventarioEliminar(null);

      showAlert(meta?.message || "Inventário eliminado com sucesso.", "success");

      await carregarInventarios();
    } catch (error) {
      showAlert(error?.message || "Erro ao eliminar o inventário.", "error");
    } finally {
      setEliminarLoading(false);
    }
  };

  const reabrirInventarioLista = async () => {
    if (!inventarioReabrir?.id || !isAdminLike || reabrirLoading) return;

    if (!inventarioReabrir.dataencerramento) {
      showAlert("Este inventário já está aberto.", "warning");
      return;
    }

    const motivo = motivoReabertura.trim();

    if (!motivo) {
      showAlert("Indica o motivo da reabertura.", "warning");
      return;
    }

    if (!idutilizador) {
      showAlert("Utilizador inválido.", "error");
      return;
    }

    setReabrirLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Stocks/ReabrirInventario", {
          id: Number(inventarioReabrir.id),
          idutilizador,
          motivo,
        })
      );

      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Não foi possível reabrir o inventário.", "error");
        return;
      }

      setInventarioReabrir(null);
      setMotivoReabertura("");

      await carregarInventarios();

      showAlert(meta?.message || "Inventário reaberto com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao reabrir o inventário.", "error");
    } finally {
      setReabrirLoading(false);
    }
  };

  const encerrarInventarioExportado = async () => {
    if (!inventarioEncerrar?.id || encerrarLoading) return;

    const idInventarioEncerrar = Number(inventarioEncerrar.id);

    if (!idutilizador) {
      showAlert("Utilizador inválido.", "error");
      return;
    }

    setEncerrarLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Stocks/EncerrarInventario", {
          id: idInventarioEncerrar,
          idutilizadorencerramento: idutilizador,
        })
      );

      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Não foi possível encerrar o inventário.", "error");
        return;
      }

      // Atualizar imediatamente o estado do inventário no diálogo de contagens.
      setInventarioContagens((anterior) =>
        Number(anterior?.id) === idInventarioEncerrar
          ? { ...anterior, dataencerramento: new Date().toISOString() }
          : anterior
      );

      setInventarioEncerrar(null);

      await carregarInventarios();

      showAlert(meta?.message || "Inventário encerrado com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao encerrar o inventário.", "error");
    } finally {
      setEncerrarLoading(false);
    }
  };

  const exportarContagensPdf = () => {
    if (!inventarioContagens || contagensLoading || contagens.length === 0) {
      showAlert("Não existem contagens para exportar.", "warning");
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const mostrarOficial = contagens.some(
        (x) => x.qtd_esperada !== null && x.qtd_esperada !== undefined
      );

      const formatarQtd = (valor) => {
        if (valor === null || valor === undefined || valor === "") return "";

        const n = Number(valor);

        return Number.isFinite(n) ? n.toLocaleString("pt-PT", { maximumFractionDigits: 3 }) : "";
      };

      const cabecalhos = [
        "Referência",
        "Material",
        "Centro",
        "Un.",
        ...(mostrarOficial ? ["Qtd. oficial"] : []),
        "Qtd. contada",
        ...(mostrarOficial ? ["Diferença"] : []),
        "Data da contagem",
        "Utilizador",
      ];

      const linhas = contagens.map((x) => [
        String(x.refmaterial || ""),
        String(x.material || ""),
        String(x.centro || ""),
        String(x.unidade || ""),
        ...(mostrarOficial ? [formatarQtd(x.qtd_esperada)] : []),
        formatarQtd(x.qtd_verificada),
        ...(mostrarOficial ? [formatarQtd(x.diferenca)] : []),
        formatData(x.data_registo),
        String(x.utilizador || ""),
      ]);

      const nomeInventario = String(inventarioContagens.nome || "Inventário");

      // Cabeçalho do relatório
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Contagens do inventário", 12, 15);

      doc.setFontSize(11);
      doc.text(nomeInventario, 12, 23);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);

      doc.text(`Centro: ${inventarioContagens.centro || "—"}`, 12, 30);
      doc.text(`Fabricante: ${inventarioContagens.fabricante || "—"}`, 12, 36);
      doc.text(`Marca: ${inventarioContagens.marca || "—"}`, 12, 42);

      const dataEmissao = new Date().toLocaleString("pt-PT");

      doc.text(`Emitido em: ${dataEmissao}`, 12, 49);
      doc.text(`Total de materiais contados: ${contagens.length}`, 285, 49, {
        align: "right",
      });

      // Tabela paginada automaticamente
      autoTable(doc, {
        head: [cabecalhos],
        body: linhas,
        startY: 54,
        margin: {
          top: 14,
          right: 10,
          bottom: 17,
          left: 10,
        },
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 7.5,
          cellPadding: 2,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [52, 71, 103],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [247, 249, 252],
        },
        columnStyles: {
          0: { cellWidth: 24 },
          1: { cellWidth: "auto" },
        },
        showHead: "everyPage",
        pageBreak: "auto",
        rowPageBreak: "avoid",
      });

      // Rodapé em todas as páginas
      const totalPaginas = doc.internal.getNumberOfPages();

      for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
        doc.setPage(pagina);

        const largura = doc.internal.pageSize.getWidth();
        const altura = doc.internal.pageSize.getHeight();

        doc.setDrawColor(210, 210, 210);
        doc.line(10, altura - 13, largura - 10, altura - 13);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);

        doc.text(nomeInventario, 12, altura - 8);
        doc.text(`Página ${pagina} de ${totalPaginas}`, largura - 12, altura - 8, {
          align: "right",
        });
      }

      const nomeFicheiro = nomeInventario.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");

      doc.save(`${nomeFicheiro}_contagens.pdf`);

      showAlert("PDF das contagens gerado com sucesso.", "success");

      // Propor encerramento apenas se o inventário estiver aberto.
      if (!inventarioContagens.dataencerramento) {
        setInventarioEncerrar({ ...inventarioContagens });
      }
    } catch (error) {
      console.error("Erro ao gerar PDF das contagens:", error);
      showAlert("Não foi possível gerar o PDF das contagens.", "error");
    }
  };

  const exportarContagensExcel = () => {
    if (!inventarioContagens || contagens.length === 0 || contagensLoading) {
      showAlert("Não existem contagens para exportar.", "warning");
      return;
    }

    try {
      const mostrarOficial = contagens.some(
        (x) => x.qtd_esperada !== null && x.qtd_esperada !== undefined
      );

      const dados = contagens.map((x) => {
        const linha = {
          Referência: String(x.refmaterial || ""),
          Material: x.material || "",
          Centro: x.centro || "",
          Unidade: x.unidade || "",
        };

        if (mostrarOficial) {
          linha["Qtd. oficial"] = x.qtd_esperada == null ? null : Number(x.qtd_esperada);
        }

        linha["Qtd. contada"] = x.qtd_verificada == null ? null : Number(x.qtd_verificada);

        if (mostrarOficial) {
          linha.Diferença = x.diferenca == null ? null : Number(x.diferenca);
        }

        linha["Data da contagem"] = formatData(x.data_registo);
        linha.Utilizador = x.utilizador || "";

        return linha;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dados);

      const colunas = mostrarOficial
        ? [17, 55, 25, 12, 17, 17, 15, 22, 30]
        : [17, 55, 25, 12, 17, 22, 30];

      ws["!cols"] = colunas.map((wch) => ({ wch }));

      if (ws["!ref"]) {
        const range = XLSX.utils.decode_range(ws["!ref"]);

        // Cabeçalhos a negrito.
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const endereco = XLSX.utils.encode_cell({ r: 0, c: col });
          const cell = ws[endereco];

          if (cell) {
            cell.s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "344767" } },
              alignment: { vertical: "center" },
            };
          }
        }

        // Formato numérico das quantidades.
        for (let row = 1; row <= range.e.r; row += 1) {
          for (let col = range.s.c; col <= range.e.c; col += 1) {
            const endereco = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = ws[endereco];

            if (cell?.t === "n") {
              cell.z = "#,##0.###";
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Contagens");

      const nome = String(inventarioContagens.nome || `Inventario_${inventarioContagens.id}`)
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, "_");

      XLSX.writeFile(wb, `${nome}_contagens.xlsx`);

      showAlert("Contagens exportadas para Excel.", "success");

      // Propor encerramento apenas se o inventário estiver aberto.
      if (!inventarioContagens.dataencerramento) {
        setInventarioEncerrar({ ...inventarioContagens });
      }
    } catch (error) {
      console.error("Erro ao exportar contagens:", error);
      showAlert("Não foi possível exportar as contagens.", "error");
    }
  };

  const columnsContagens = useMemo(
    () => [
      {
        Header: "Referência",
        accessor: "refmaterial",
        width: 135,
        minWidth: 110,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 360,
        minWidth: 220,
      },
      {
        Header: "Centro",
        accessor: "centro",
        width: 170,
        minWidth: 120,
      },
      {
        Header: "Unidade",
        accessor: "unidade",
        width: 100,
        minWidth: 80,
      },
      {
        Header: "Qtd. oficial",
        accessor: "qtd_esperada",
        width: 130,
        minWidth: 110,
        sortType: "numPt",
        align: "right",
        Cell: ({ value }) =>
          value == null ? "—" : Number(value).toLocaleString("pt-PT", { maximumFractionDigits: 3 }),
      },
      {
        Header: "Qtd. contada",
        accessor: "qtd_verificada",
        width: 130,
        minWidth: 110,
        sortType: "numPt",
        align: "right",
        Cell: ({ value }) =>
          value == null ? "—" : Number(value).toLocaleString("pt-PT", { maximumFractionDigits: 3 }),
      },
      {
        Header: "Diferença",
        accessor: "diferenca",
        width: 120,
        minWidth: 100,
        sortType: "numPt",
        align: "right",
        Cell: ({ value }) => {
          if (value == null) return "—";

          const n = Number(value);

          return (
            <span
              style={{
                fontWeight: 600,
                color: n < 0 ? "#d32f2f" : n > 0 ? "#2e7d32" : undefined,
              }}
            >
              {n.toLocaleString("pt-PT", { maximumFractionDigits: 3 })}
            </span>
          );
        },
      },
      {
        Header: "Data da contagem",
        accessor: "data_registo",
        width: 165,
        minWidth: 145,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Utilizador",
        accessor: "utilizador",
        width: 190,
        minWidth: 140,
      },
    ],
    []
  );

  const columnsInventarios = useMemo(
    () => [
      //   { Header: "ID", accessor: "id", width: 70, minWidth: 60, sortType: "numPt" },
      { Header: "Nome", accessor: "nome", width: 220, minWidth: 160 },
      { Header: "Centro", accessor: "centro", width: 180, minWidth: 140 },
      { Header: "Fabricante", accessor: "fabricante", width: 260, minWidth: 180 },
      { Header: "Marca", accessor: "marca", width: 220, minWidth: 160 },
      {
        Header: "Abertura",
        accessor: "dataabertura",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
      {
        Header: "Encerramento",
        accessor: "dataencerramento",
        width: 150,
        minWidth: 130,
        Cell: ({ value }) => formatData(value),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <MDBox mt={2}>
        <ProjectTable
          tableKey="inventarios-lista"
          version="v1"
          title={`Inventários (${inventarios.length})`}
          columns={columnsInventarios}
          data={inventarios}
          loading={loading}
          minWidth={1300}
          maxHeight="48vh"
          emptyMessage="Sem inventários para apresentar."
          defaultSortBy={[{ id: "dataabertura", desc: true }]}
          toolbarLeft={
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirEncerrados}
                    onChange={(e) => setIncluirEncerrados(e.target.checked)}
                    size="small"
                  />
                }
                label={<span style={{ fontSize: 12 }}>Incluir encerrados</span>}
              />
            </>
          }
          toolbarActions={[
            {
              id: "criar-inventario",
              label: "Criar inventário",
              color: "success",
              onClick: () => setOpenNovoInventario(true),
            },
            {
              id: "historico-material",
              label: "Histórico por material",
              color: "info",
              onClick: () => setOpenHistoricoMaterial(true),
            },
          ]}
          rowActions={[
            {
              id: "abrir",
              icon: <EditOutlinedIcon fontSize="small" />,
              tooltip: (row) =>
                row.dataencerramento ? "Consultar inventário encerrado" : "Abrir inventário",
              color: "warning",
              visible: (row) => !row.dataencerramento || isAdminLike,
              onClick: abrirInventario,
            },
            {
              id: "contagens",
              icon: <FactCheckOutlinedIcon fontSize="small" />,
              tooltip: "Ver contagens",
              color: "info",
              onClick: abrirContagens,
            },
            {
              id: "encerrar",
              icon: <LockOpenIcon fontSize="small" />,
              tooltip: "Encerrar inventário",
              color: "dark",
              visible: (row) => !row.dataencerramento,
              onClick: (row) => setInventarioEncerrar(row),
            },
            {
              id: "reabrir",
              icon: <LockIcon fontSize="small" />,
              tooltip: "Reabrir inventário",
              color: "dark",
              visible: (row) => Boolean(row.dataencerramento) && isAdminLike,
              onClick: (row) => {
                setMotivoReabertura("");
                setInventarioReabrir(row);
              },
            },
            ...(isAdminLike
              ? [
                  {
                    id: "eliminar",
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    tooltip: "Eliminar inventário",
                    color: "error",
                    onClick: (row) => setInventarioEliminar(row),
                  },
                ]
              : []),
          ]}
        />

        <DialogInventarioHistoricoMaterial
          open={openHistoricoMaterial}
          onClose={() => setOpenHistoricoMaterial(false)}
        />

        <Dialog
          open={openNovoInventario}
          onClose={() => setOpenNovoInventario(false)}
          maxWidth="sm"
          fullWidth
          PaperComponent={PaperComponent}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Novo inventário
          </DialogTitle>

          <DialogContent dividers>
            <MDBox display="grid" gap={2} pt={0.5}>
              <MDBox>
                <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  Tipo de inventário
                </MDTypography>

                <RadioGroup
                  value={modoInventario}
                  onChange={(e) => {
                    setModoInventario(e.target.value);
                    setFabricante(null);
                    setMarcasSelecionadas([]);
                    setMarcas([]);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 0, sm: 2 },
                  }}
                >
                  <FormControlLabel
                    value="MARCAS"
                    control={<Radio />}
                    label="Por fabricante e marca"
                  />

                  <FormControlLabel
                    value="LIVRE"
                    control={<Radio />}
                    label="Livre (qualquer material)"
                  />
                </RadioGroup>
              </MDBox>

              <Autocomplete
                options={centros}
                filterOptions={createAutocompleteFilter((o) =>
                  o?.centro ? `${o.centro} ${o.refcentro}` : o?.refcentro || ""
                )}
                value={centro}
                onChange={(_, v) => setCentro(v)}
                getOptionLabel={(o) =>
                  o?.centro ? `${o.centro} (${o.refcentro})` : o?.refcentro ? o.refcentro : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.refcentro || "") === String(val?.refcentro || "")
                }
                renderInput={(params) => <TextField {...params} label="Centro" size="small" />}
              />

              {modoInventario === "MARCAS" && (
                <>
                  <Autocomplete
                    options={fabricantes}
                    filterOptions={createAutocompleteFilter((o) =>
                      o?.nome ? `${o.nome} ${o.ref}` : o?.ref || ""
                    )}
                    value={fabricante}
                    disabled={!centro}
                    onChange={(_, v) => {
                      setFabricante(v);
                      carregarMarcas(v?.ref || "");
                    }}
                    getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                    isOptionEqualToValue={(opt, val) =>
                      String(opt?.ref || "") === String(val?.ref || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Fabricante" size="small" />
                    )}
                  />

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={marcas}
                    filterOptions={createAutocompleteFilter((o) =>
                      o?.nome ? `${o.nome} ${o.ref}` : o?.ref || ""
                    )}
                    value={marcasSelecionadas}
                    disabled={!fabricante}
                    onChange={(_, novasMarcas) => {
                      setMarcasSelecionadas(novasMarcas);
                    }}
                    getOptionLabel={(o) => (o?.nome ? `${o.nome} (${o.ref})` : "")}
                    isOptionEqualToValue={(opt, val) =>
                      String(opt?.ref || "") === String(val?.ref || "")
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.ref}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option.nome} ({option.ref})
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Marcas"
                        size="small"
                        placeholder={marcasSelecionadas.length === 0 ? "Selecionar marcas..." : ""}
                        helperText={`${marcasSelecionadas.length} marca(s) selecionada(s)`}
                      />
                    )}
                  />
                </>
              )}
            </MDBox>
          </DialogContent>

          <DialogActions sx={{ flexWrap: "wrap" }}>
            <MDButton color="secondary" onClick={() => setOpenNovoInventario(false)}>
              Cancelar
            </MDButton>

            <MDButton
              color="info"
              onClick={criarInventario}
              disabled={
                !centro ||
                (modoInventario === "MARCAS" && (!fabricante || marcasSelecionadas.length === 0))
              }
            >
              Criar inventário
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(inventarioContagens)}
          onClose={() => {
            setInventarioContagens(null);
            setContagens([]);
          }}
          maxWidth="xl"
          fullWidth
          PaperComponent={PaperComponent}
          PaperProps={{
            sx: {
              width: "calc(100% - 16px)",
              maxWidth: "1500px",
              maxHeight: "94vh",
              m: 1,
            },
          }}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Contagens do inventário
          </DialogTitle>

          <DialogContent dividers sx={{ px: { xs: 1, sm: 2 } }}>
            <MDBox mb={2}>
              <MDTypography variant="h6" fontWeight="bold">
                {inventarioContagens?.nome || ""}
              </MDTypography>

              <MDTypography variant="caption" color="text" display="block">
                Centro: {inventarioContagens?.centro || "—"}
                {" | "}Fabricante: {inventarioContagens?.fabricante || "—"}
                {" | "}Marca: {inventarioContagens?.marca || "—"}
              </MDTypography>
            </MDBox>

            <ProjectTable
              tableKey={`inventario-contagens-${inventarioContagens?.id || 0}`}
              version="v1"
              title={`Materiais contados (${contagens.length})`}
              columns={columnsContagens}
              data={contagens}
              loading={contagensLoading}
              minWidth={1350}
              maxHeight="55vh"
              defaultSortBy={[{ id: "data_registo", desc: true }]}
              emptyMessage="Ainda não existem contagens registadas neste inventário."
              showKeepFilters
              toolbarLeft={
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Tooltip title="Exportar para PDF" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Exportar para PDF"
                        disabled={contagensLoading || contagens.length === 0}
                        onClick={exportarContagensPdf}
                        sx={{ color: "#1a73e8" }}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Exportar para Excel" arrow>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Exportar para Excel"
                        disabled={contagensLoading || contagens.length === 0}
                        onClick={exportarContagensExcel}
                        sx={{ color: "#1a73e8" }}
                      >
                        <GridOnIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </MDBox>
              }
            />
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <MDButton
              color="secondary"
              onClick={() => {
                setInventarioContagens(null);
                setContagens([]);
              }}
            >
              Fechar
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Reabertura de inventário — exclusiva de isAdminLike */}
        <Dialog
          open={Boolean(inventarioReabrir) && isAdminLike}
          onClose={() => {
            if (reabrirLoading) return;
            setInventarioReabrir(null);
            setMotivoReabertura("");
          }}
          maxWidth="sm"
          fullWidth
          PaperComponent={PaperComponent}
          PaperProps={{
            sx: {
              width: "calc(100% - 16px)",
              maxWidth: "550px",
              maxHeight: "calc(100dvh - 32px)",
              m: 1,
            },
          }}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Reabrir inventário
          </DialogTitle>

          <DialogContent dividers>
            <MDBox display="grid" gap={2}>
              <MDTypography variant="body2">Pretende reabrir o seguinte inventário?</MDTypography>

              <MDBox>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  display="block"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  #{inventarioReabrir?.id} — {inventarioReabrir?.nome}
                </MDTypography>

                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Centro: {inventarioReabrir?.centro || "—"}
                </MDTypography>
              </MDBox>

              <MDTypography variant="body2" color="warning" fontWeight="bold">
                A reabertura permitirá voltar a registar, substituir e anular contagens neste
                inventário.
              </MDTypography>

              <MDTypography variant="body2" color="text">
                O encerramento anterior ficará registado no histórico. A reabertura poderá ser
                recusada se existirem conflitos com outros inventários abertos no mesmo centro.
              </MDTypography>

              <TextField
                label="Motivo da reabertura"
                value={motivoReabertura}
                onChange={(e) => setMotivoReabertura(e.target.value)}
                multiline
                minRows={2}
                required
                fullWidth
                autoFocus
                disabled={reabrirLoading}
                helperText="O motivo e o utilizador responsável ficarão registados no histórico."
              />
            </MDBox>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2,
              py: 1.5,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <MDButton
              color="secondary"
              variant="outlined"
              disabled={reabrirLoading}
              onClick={() => {
                setInventarioReabrir(null);
                setMotivoReabertura("");
              }}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Cancelar
            </MDButton>

            <MDButton
              color="success"
              disabled={reabrirLoading || !motivoReabertura.trim()}
              onClick={reabrirInventarioLista}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {reabrirLoading ? "A reabrir..." : "Confirmar reabertura"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Confirmar encerramento após exportação PDF/Excel */}
        <Dialog
          open={Boolean(inventarioEncerrar)}
          onClose={() => {
            if (!encerrarLoading) setInventarioEncerrar(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperComponent={PaperComponent}
          PaperProps={{
            sx: {
              width: "calc(100% - 16px)",
              maxWidth: "550px",
              maxHeight: "calc(100dvh - 32px)",
              m: 1,
            },
          }}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Encerrar inventário
          </DialogTitle>

          <DialogContent dividers>
            <MDBox display="grid" gap={2}>
              <MDTypography variant="body2">Pretende encerrar este inventário?</MDTypography>

              <MDBox>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  display="block"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  #{inventarioEncerrar?.id} — {inventarioEncerrar?.nome}
                </MDTypography>

                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Centro: {inventarioEncerrar?.centro || "—"}
                </MDTypography>
              </MDBox>

              <MDTypography variant="body2" color="error" fontWeight="bold">
                Após o encerramento, não será possível registar, modificar ou anular contagens neste
                inventário.
              </MDTypography>

              <MDTypography variant="caption" color="text">
                Antes de encerrar, confirma que as contagens estão concluídas e que os dados
                necessários à Contabilidade foram exportados. O encerramento não altera o stock
                oficial no UNIDIS.
              </MDTypography>
            </MDBox>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2,
              py: 1.5,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <MDButton
              color="secondary"
              variant="outlined"
              disabled={encerrarLoading}
              onClick={() => setInventarioEncerrar(null)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Manter aberto
            </MDButton>

            <MDButton
              color="error"
              disabled={encerrarLoading}
              onClick={encerrarInventarioExportado}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {encerrarLoading ? "A encerrar..." : "Encerrar inventário"}
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(inventarioEliminar)}
          onClose={() => {
            if (!eliminarLoading) setInventarioEliminar(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperComponent={PaperComponent}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: "move" }}>
            Eliminar inventário
          </DialogTitle>

          <DialogContent dividers>
            <MDTypography variant="body2" mb={2}>
              Tens a certeza de que pretendes eliminar definitivamente este inventário?
            </MDTypography>

            <MDBox p={2} borderRadius="lg" sx={{ backgroundColor: "rgba(0, 0, 0, 0.035)" }}>
              <MDTypography variant="button" fontWeight="bold" display="block">
                {inventarioEliminar?.nome || ""}
              </MDTypography>

              <MDTypography variant="caption" color="text" display="block" mt={1}>
                Centro: {inventarioEliminar?.centro || "—"}
              </MDTypography>

              <MDTypography variant="caption" color="text" display="block">
                Fabricante: {inventarioEliminar?.fabricante || "—"}
              </MDTypography>

              <MDTypography variant="caption" color="text" display="block">
                Marca: {inventarioEliminar?.marca || "—"}
              </MDTypography>
            </MDBox>

            <MDTypography variant="body2" color="error" fontWeight="bold" mt={2}>
              Atenção: esta operação é irreversível e também elimina todas as contagens registadas
              neste inventário.
            </MDTypography>

            <MDTypography variant="caption" color="text" display="block" mt={1}>
              As quantidades oficiais de stock não serão alteradas.
            </MDTypography>
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: "wrap" }}>
            <MDButton
              color="secondary"
              variant="outlined"
              disabled={eliminarLoading}
              onClick={() => setInventarioEliminar(null)}
            >
              Cancelar
            </MDButton>

            <MDButton color="error" disabled={eliminarLoading} onClick={eliminarInventario}>
              {eliminarLoading ? "A eliminar..." : "Eliminar definitivamente"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}
